import React, {FC, memo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {ConversationHighlightModel} from '../../../../../models/api/conversationHighlightModel';
import {VisualConversationSendersModel} from '../../../../../models/api/conversationSendersModel';
import {shrinkFlexItemFirst, shrinkFlexItemLast} from '../../../../common/formatters/senders/common';
import {SendersFormatter} from '../../../../common/formatters/sendersFormatter';
import {CellRecipientTeammatesProps, StyledAssigneeBaseDiv, StyledAssigneeChevronDiv} from './cellRecipient';

/*
 * Props.
 */

interface CellRecipientSendersProps extends CellRecipientTeammatesProps {
  conversationSenders: VisualConversationSendersModel;
  conversationHighlight: ConversationHighlightModel | undefined;

  assignee: TeammateModel | undefined;
  isSelected: boolean | undefined;
  isUnread: boolean | undefined;
  userId: number | undefined;
  isUserTracking: boolean;

  isDiscussion: boolean;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  margin-left: -3px;
`;

interface SendersStyleProps {
  $isUnread: boolean;
}

const StyledSendersSpan = styled.span<SendersStyleProps>`
  // Roughly the size of one char and the ellipsis character.
  min-width: 3ch;

  // We want to shrink the assignee all the way before skrinking the assignee.
  ${shrinkFlexItemLast()}

  font-weight: ${(p) => (p.$isUnread ? fontWeights.bold : fontWeights.semibold)};
`;

const StyledAssigneeDiv = styled(StyledAssigneeBaseDiv)`
  ${ellipsis()}
  min-width: 0;

  // We want to shrink the assignee all the way before skrinking the assignee.
  ${shrinkFlexItemFirst()}
`;

/*
 * Component.
 */

export const CellRecipientSenders: FC<CellRecipientSendersProps> = memo((props) => {
  const {
    conversation,
    conversationSenders,
    isSelected,
    isUnread,
    assignee,
    isDiscussion,
    trackerTeammates,
    lastCommentAuthor,
    isUserTracking,
    conversationHighlight,
    userId,
  } = props;

  return (
    <StyledWrapperDiv>
      <StyledSendersSpan key="senders" $isUnread={Boolean(isUnread)}>
        <SendersFormatter
          conversation={conversation}
          conversationSenders={conversationSenders}
          isCellSelected={Boolean(isSelected)}
          hasAssignee={Boolean(assignee)}
          isDiscussion={isDiscussion}
          trackerTeammates={trackerTeammates}
          lastCommentAuthor={lastCommentAuthor}
          isUserTracking={isUserTracking}
          conversationHighlight={conversationHighlight}
          userId={userId}
        />
      </StyledSendersSpan>
      {maybeRenderAssignee(props)}
    </StyledWrapperDiv>
  );
});

function maybeRenderAssignee(props: CellRecipientSendersProps) {
  if (!props.assignee) {
    return null;
  }

  return [
    <StyledAssigneeChevronDiv
      key="assignee-chevron"
      $isSelected={Boolean(props.isSelected)}
      $isUnread={Boolean(props.isUnread)}
    >
      <Icon name="chevronRightSmall" />
    </StyledAssigneeChevronDiv>,
    <StyledAssigneeDiv
      key="assignee"
      $isSelected={Boolean(props.isSelected)}
      $isUnread={Boolean(props.isUnread)}
    >
      {props.userId === props.assignee.id ? (
        <Trans id="assignee-me">Me</Trans>
      ) : (
        buildTeammateDisplayName(props.assignee)
      )}
    </StyledAssigneeDiv>,
  ];
}
